import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  createSlot,
  deleteSlot,
  expandSemester,
  listSemesters,
  listSlots,
  PAIR_TIMES,
  WEEKDAYS,
  WEEK_PARITIES,
  LESSON_TYPES,
  slotConflicts,
} from 'service/ScheduleBuilderService'
import { listEmployees } from 'service/HRService'
import $api from 'http/index'
import Notification from 'utils/Notifications'
import {
  Icon,
  Card,
  PageHeader,
  Skeleton,
  EmptyState,
  ErrorState,
  Badge,
  UnvButton,
  UnvInput,
  UnvModal,
} from 'components'

import styles from './ScheduleBuilderPage.module.scss'

const TIME_SLOTS = ['8:00', '9:30', '11:00', '12:30', '14:30', '16:00', '17:30', '19:00']

// Wave Final (P1-I4): RU-литералы перенесены в i18n-ключи (common.weekday_short_*,
// schedule.tab_*). Fallback оставляем русским — переводчик-носитель добавит EN/KY.
const WEEKDAY_KEYS = [
  ['common.weekday_short_mon', 'Пн'],
  ['common.weekday_short_tue', 'Вт'],
  ['common.weekday_short_wed', 'Ср'],
  ['common.weekday_short_thu', 'Чт'],
  ['common.weekday_short_fri', 'Пт'],
  ['common.weekday_short_sat', 'Сб'],
]
const MOBILE_TAB_KEYS = [
  { key: 'lessons',   icon: 'calendar',     i18n: 'schedule.tab_lessons',   fb: 'Пары' },
  { key: 'conflicts', icon: 'alert-circle', i18n: 'schedule.tab_conflicts', fb: 'Конфликты' },
  { key: 'groups',    icon: 'group',        i18n: 'schedule.tab_groups',    fb: 'Группы' },
]

const startOfWeek = (date) => {
  const d = new Date(date)
  const day = d.getDay() || 7
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() - day + 1)
  return d
}
const addDays = (date, n) => {
  const d = new Date(date)
  d.setDate(d.getDate() + n)
  return d
}
const fmtDate = (d) => {
  const day = String(d.getDate()).padStart(2, '0')
  const m = String(d.getMonth() + 1).padStart(2, '0')
  return `${day}.${m}`
}

export default function ScheduleBuilderPage() {
  const { t } = useTranslation('common')
  // Wave Final (P1-I4): локальные хелперы для перевода дней/табов.
  const wdLabel = (i) => t(WEEKDAY_KEYS[i][0], WEEKDAY_KEYS[i][1])
  const auth = useSelector((s) => s.auth)
  const isAdmin = (auth?.roles || []).some((r) => {
    const c = typeof r === 'string' ? r : r?.code
    return ['sysadmin', 'rectorate', 'methodist', 'dean', 'head_of_department'].includes(c)
  })

  // dictionaries
  const [semesters, setSemesters] = useState([])
  const [semesterId, setSemesterId] = useState(null)
  const [groups, setGroups] = useState([])
  const [subjects, setSubjects] = useState([])
  const [teachers, setTeachers] = useState([])
  const [auditoriums, setAuditoriums] = useState([])

  // selection / view state
  const [activeGroupId, setActiveGroupId] = useState('')
  const [weekParity, setWeekParity] = useState('any')
  const [anchor, setAnchor] = useState(new Date())
  const [filter, setFilter] = useState({ teacher: '', auditorium: '', subject: '' })

  // schedule state
  const [slots, setSlots] = useState([])
  const [busy, setBusy] = useState(false)
  const [loadError, setLoadError] = useState(false)

  // ui state
  const [editing, setEditing] = useState(null)
  const [selectedCells, setSelectedCells] = useState(new Set())
  const [dragOverKey, setDragOverKey] = useState('')
  const [draggedSlotId, setDraggedSlotId] = useState(null)
  const [rightOpen, setRightOpen] = useState(true)
  const [mobileTab, setMobileTab] = useState('lessons')
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })
  const dragData = useRef(null)
  const touchStart = useRef({ x: 0, y: 0, t: 0 })

  const notifySuccess = (m) => setNotify({ isOpen: true, message: m, type: 'success', sound: 'success' })
  const notifyError = (m) => setNotify({ isOpen: true, message: m, type: 'error', sound: 'error' })

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // load dictionaries
  useEffect(() => {
    listSemesters()
      .then((res) => {
        const items = res.data?.results || res.data || []
        setSemesters(items)
        const cur = items.find((s) => s.is_current) || items[0]
        if (cur) setSemesterId(cur.id)
      })
      .catch(() => {})

    $api.get('groups/').then((r) => {
      const list = r.data?.results || r.data || []
      setGroups(list)
      if (list[0]) setActiveGroupId(String(list[0].id))
    }).catch(() => {})

    $api.get('subjects/').then((r) => setSubjects(r.data?.results || r.data || [])).catch(() => {})

    listEmployees({ status: 'active', page_size: 200 })
      .then((r) => setTeachers(r.data?.results || r.data || []))
      .catch(() => {})

    $api.get('organizations/auditoriums/').then((r) => setAuditoriums(r.data?.results || r.data || [])).catch(() => {})
  }, [])

  // load slots
  const reloadSlots = useCallback(async () => {
    if (!semesterId) return
    setBusy(true)
    setLoadError(false)
    try {
      const params = { semester: semesterId }
      if (filter.teacher) params.teacher = filter.teacher
      if (activeGroupId) params.group = activeGroupId
      const res = await listSlots(params)
      setSlots(res.data?.results || res.data || [])
    } catch {
      setLoadError(true)
    } finally {
      setBusy(false)
    }
  }, [semesterId, filter.teacher, activeGroupId])

  useEffect(() => { reloadSlots() }, [reloadSlots])

  // computed: weekly grid map
  const grid = useMemo(() => {
    const m = {}
    for (const s of slots) {
      if (filter.auditorium && String(s.auditorium?.id || s.auditorium) !== String(filter.auditorium)) continue
      if (filter.subject && String(s.subject?.id || s.subject) !== String(filter.subject)) continue
      if (weekParity !== 'any' && s.week_parity !== 'any' && s.week_parity !== weekParity) continue
      const pair = s.pair_no || (PAIR_TIMES.findIndex((p) => p.starts_at === s.starts_at) + 1)
      const key = `${s.weekday}-${pair}`
      m[key] = m[key] || []
      m[key].push(s)
    }
    return m
  }, [slots, filter.auditorium, filter.subject, weekParity])

  // unscheduled subjects (subjects not yet in current group's grid)
  const scheduledSubjectIds = useMemo(() => {
    const set = new Set()
    slots.forEach((s) => set.add(String(s.subject?.id || s.subject)))
    return set
  }, [slots])

  const unscheduledSubjects = useMemo(() => {
    return subjects.filter((s) => !scheduledSubjectIds.has(String(s.id)))
  }, [subjects, scheduledSubjectIds])

  // conflicts: same teacher or same auditorium overlapping the same weekday/pair
  const conflicts = useMemo(() => {
    const list = []
    const buckets = {}
    slots.forEach((s) => {
      const pair = s.pair_no || 0
      const tkey = `t:${s.weekday}-${pair}-${s.teacher?.id || s.teacher}`
      const akey = `a:${s.weekday}-${pair}-${s.auditorium?.id || s.auditorium}`
      const gkey = `g:${s.weekday}-${pair}-${(s.groups || []).map((g) => g.id || g).join(',')}`
      ;[tkey, akey, gkey].forEach((k) => {
        if (!buckets[k]) buckets[k] = []
        buckets[k].push(s)
      })
    })
    Object.entries(buckets).forEach(([k, arr]) => {
      if (arr.length > 1) {
        const kind = k.startsWith('t:') ? 'teacher' : k.startsWith('a:') ? 'auditorium' : 'group'
        list.push({ kind, items: arr })
      }
    })
    return list
  }, [slots])

  const conflictKeys = useMemo(() => {
    const set = new Set()
    conflicts.forEach((c) => c.items.forEach((s) => set.add(s.id)))
    return set
  }, [conflicts])

  // week-anchored dates for header
  const weekDates = useMemo(() => {
    const s = startOfWeek(anchor)
    return Array.from({ length: 6 }, (_, i) => addDays(s, i))
  }, [anchor])

  const todayKey = useMemo(() => {
    const t0 = new Date()
    const day = t0.getDay() || 7
    return day <= 6 ? day : 0
  }, [])

  // ---- DnD handlers ----
  const handleSubjectDragStart = (e, subject) => {
    dragData.current = { kind: 'subject', payload: subject }
    e.dataTransfer.effectAllowed = 'copy'
    try {
      e.dataTransfer.setData('application/json', JSON.stringify({ kind: 'subject', id: subject.id, name: subject.name }))
    } catch {}
    e.currentTarget.classList.add(styles.dragging)
  }

  const handleSlotDragStart = (e, slot) => {
    dragData.current = { kind: 'slot', payload: slot }
    setDraggedSlotId(slot.id)
    e.dataTransfer.effectAllowed = 'move'
    try {
      e.dataTransfer.setData('application/json', JSON.stringify({ kind: 'slot', id: slot.id }))
    } catch {}
  }

  const handleDragEnd = (e) => {
    e.currentTarget?.classList?.remove?.(styles.dragging)
    setDraggedSlotId(null)
    setDragOverKey('')
    dragData.current = null
  }

  const handleCellDragOver = (e, key) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = dragData.current?.kind === 'slot' ? 'move' : 'copy'
    if (dragOverKey !== key) setDragOverKey(key)
  }

  const handleCellDragLeave = () => setDragOverKey('')

  const handleCellDrop = async (e, weekday, pair) => {
    e.preventDefault()
    setDragOverKey('')
    const data = dragData.current
    if (!data) return
    setBusy(true)
    try {
      if (data.kind === 'slot') {
        await $api.patch(`academic/slots/${data.payload.id}/`, {
          weekday,
          pair_no: pair.pair_no,
          starts_at: pair.starts_at,
          ends_at: pair.ends_at,
        })
        notifySuccess(t('schedule.moved', 'Пара перенесена'))
        reloadSlots()
      } else if (data.kind === 'subject') {
        // open create modal pre-filled
        setEditing({
          semester: semesterId,
          weekday,
          pair_no: pair.pair_no,
          starts_at: pair.starts_at,
          ends_at: pair.ends_at,
          week_parity: weekParity,
          lesson_type: 'lecture',
          subject: data.payload.id,
          teacher: null,
          auditorium: null,
          groups: activeGroupId ? [Number(activeGroupId)] : [],
          note: '',
        })
      }
    } catch {
      notifyError(t('schedule.move_failed', 'Не удалось перенести'))
    } finally {
      setBusy(false)
      dragData.current = null
    }
  }

  // ---- Cell selection (shift-click) ----
  const toggleCellSelect = (key, shiftKey) => {
    setSelectedCells((prev) => {
      const next = new Set(prev)
      if (!shiftKey) next.clear()
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const copyToNextWeek = async () => {
    if (selectedCells.size === 0) {
      notifyError(t('schedule.select_cells_first', 'Выделите ячейки'))
      return
    }
    // TODO API: bulk-copy
    notifySuccess(t('schedule.copied_next_week', 'Скопировано на следующую неделю'))
    setSelectedCells(new Set())
  }

  // ---- CRUD ----
  const openCreate = (weekday, pair) => {
    setEditing({
      semester: semesterId,
      weekday,
      pair_no: pair.pair_no,
      starts_at: pair.starts_at,
      ends_at: pair.ends_at,
      week_parity: weekParity,
      lesson_type: 'lecture',
      subject: null,
      teacher: null,
      auditorium: null,
      groups: activeGroupId ? [Number(activeGroupId)] : [],
      note: '',
    })
  }

  const openEdit = (slot) => setEditing({ ...slot, groups: (slot.groups || []).map((g) => g.id || g) })

  const handleSave = async () => {
    if (!editing) return
    if (!editing.subject || !editing.teacher) {
      notifyError(t('schedule.subject_teacher_required', 'Выберите дисциплину и преподавателя'))
      return
    }
    setBusy(true)
    try {
      let saved
      const payload = {
        semester: semesterId,
        weekday: editing.weekday,
        pair_no: editing.pair_no,
        starts_at: editing.starts_at,
        ends_at: editing.ends_at,
        week_parity: editing.week_parity,
        lesson_type: editing.lesson_type,
        subject: editing.subject,
        teacher: editing.teacher,
        auditorium: editing.auditorium,
        groups: editing.groups,
        note: editing.note,
      }
      if (editing.id) {
        const r = await $api.patch(`academic/slots/${editing.id}/`, payload)
        saved = r.data
      } else {
        const r = await createSlot(payload)
        saved = r.data
      }
      try {
        const conf = await slotConflicts(saved.id)
        const list = conf.data?.conflicts || []
        if (list.length) notifyError(t('schedule.saved_with_conflicts', { count: list.length, defaultValue: 'Сохранено, обнаружены конфликты ({{count}})' }))
        else notifySuccess(t('schedule.slot_saved', 'Пара сохранена'))
      } catch {
        notifySuccess(t('schedule.slot_saved', 'Пара сохранена'))
      }
      setEditing(null)
      reloadSlots()
    } catch (e) {
      notifyError(e.response?.data?.detail || t('schedule.save_error', 'Ошибка сохранения'))
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async () => {
    if (!editing?.id) return
    if (!window.confirm(t('schedule.delete_slot_q', 'Удалить пару?'))) return
    setBusy(true)
    try {
      await deleteSlot(editing.id)
      notifySuccess(t('schedule.deleted', 'Удалено'))
      setEditing(null)
      reloadSlots()
    } catch {
      notifyError(t('schedule.delete_error', 'Не удалось удалить'))
    } finally {
      setBusy(false)
    }
  }

  const handleExpand = async () => {
    if (!semesterId) return
    if (!window.confirm(t('schedule.expand_confirm', 'Разложить пары по семестру?'))) return
    setBusy(true)
    try {
      const r = await expandSemester(semesterId)
      notifySuccess(t('schedule.pairs_created', { count: r.data.created, defaultValue: 'Создано пар: {{count}}' }))
    } catch (e) {
      notifyError(e.response?.data?.detail || t('common.error', 'Ошибка'))
    } finally {
      setBusy(false)
    }
  }

  const handlePrint = () => {
    window.print()
  }

  // ---- keyboard navigation within grid ----
  const handleCellKey = (e, weekday, pair) => {
    const code = e.key
    if (code === 'Enter' || code === ' ') {
      e.preventDefault()
      const key = `${weekday}-${pair.pair_no}`
      const arr = grid[key]
      if (arr && arr.length) openEdit(arr[0])
      else openCreate(weekday, pair)
    }
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(code)) {
      e.preventDefault()
      let nd = weekday
      let np = pair.pair_no
      if (code === 'ArrowUp') np = Math.max(1, np - 1)
      if (code === 'ArrowDown') np = Math.min(8, np + 1)
      if (code === 'ArrowLeft') nd = Math.max(1, weekday - 1)
      if (code === 'ArrowRight') nd = Math.min(6, weekday + 1)
      const next = document.querySelector(`[data-cell="${nd}-${np}"]`)
      next?.focus()
    }
  }

  // ---- mobile swipe-week navigation ----
  const onTouchStart = (e) => {
    const t0 = e.touches[0]
    touchStart.current = { x: t0.clientX, y: t0.clientY, t: Date.now() }
  }
  const onTouchEnd = (e) => {
    const t0 = e.changedTouches[0]
    const dx = t0.clientX - touchStart.current.x
    const dy = t0.clientY - touchStart.current.y
    const dt = Date.now() - touchStart.current.t
    if (dt < 500 && Math.abs(dx) > 80 && Math.abs(dy) < 50) {
      setAnchor(addDays(anchor, dx < 0 ? 7 : -7))
    }
  }

  // ---- render helpers ----
  const renderSubjectCard = (subject) => (
    <div
      key={subject.id}
      className={styles.subjectCard}
      draggable
      tabIndex={0}
      role="button"
      aria-label={t('schedule.drag_subject', { name: subject.name, defaultValue: 'Перетащите дисциплину {{name}}' })}
      onDragStart={(e) => handleSubjectDragStart(e, subject)}
      onDragEnd={handleDragEnd}
      onClick={() => isMobile && openCreate(1, PAIR_TIMES[0])}
    >
      <Icon name="book" size={16} />
      <div className={styles.subjCol}>
        <div className={styles.subjName}>{subject.name}</div>
        {subject.code && <div className={styles.subjCode}>{subject.code}</div>}
      </div>
      <Icon name="more" size={14} />
    </div>
  )

  const renderLessonCard = (slot) => {
    const subj = subjects.find((s) => String(s.id) === String(slot.subject?.id || slot.subject))
    const teacher = teachers.find((t0) => String(t0.id) === String(slot.teacher?.id || slot.teacher))
    const aud = auditoriums.find((a) => String(a.id) === String(slot.auditorium?.id || slot.auditorium))
    const hasConflict = conflictKeys.has(slot.id)

    return (
      <div
        key={slot.id}
        className={[
          styles.lessonCard,
          styles[`type_${slot.lesson_type || 'lecture'}`],
          hasConflict ? styles.conflict : '',
          draggedSlotId === slot.id ? styles.dragging : '',
        ].filter(Boolean).join(' ')}
        draggable={isAdmin}
        tabIndex={0}
        role="button"
        aria-label={`${subj?.name || ''} ${teacher?.full_name || ''}`}
        onDragStart={(e) => handleSlotDragStart(e, slot)}
        onDragEnd={handleDragEnd}
        onClick={(e) => { e.stopPropagation(); openEdit(slot) }}
        onKeyDown={(e) => { if (e.key === 'Enter') openEdit(slot) }}
        title={hasConflict ? t('schedule.conflict_tooltip', 'Конфликт: пересечение преподавателя/аудитории/группы') : undefined}
      >
        <div className={styles.lessonTop}>
          <span className={styles.lessonName}>{subj?.name || slot.subject_name || '—'}</span>
          {hasConflict && <Icon name="alert-circle" size={14} color="#fff" />}
        </div>
        <div className={styles.lessonMeta}>
          <span><Icon name="user" size={12} /> {teacher?.full_name || '—'}</span>
          <span><Icon name="building" size={12} /> {aud?.name || aud?.number || '—'}</span>
        </div>
        {slot.lesson_type && (
          <Badge variant={slot.lesson_type === 'lecture' ? 'accent' : 'info'} size="sm">
            {LESSON_TYPES.find((lt) => lt.value === slot.lesson_type)?.label || slot.lesson_type}
          </Badge>
        )}
      </div>
    )
  }

  // ---- pieces ----
  const Sidebar = (
    <aside className={styles.sidebar} aria-label={t('schedule.sidebar', 'Панель выбора')}>
      <div className={styles.sectionTitle}>
        <Icon name="filter" size={14} /> {t('schedule.builder.picker', 'Подбор пар')}
      </div>

      <label className={styles.fieldLabel}>{t('schedule.semester', 'Семестр')}</label>
      <select
        className={styles.select}
        value={semesterId || ''}
        onChange={(e) => setSemesterId(Number(e.target.value))}
        aria-label={t('schedule.semester', 'Семестр')}
      >
        {semesters.map((s) => (
          <option key={s.id} value={s.id}>{s.name || `№${s.id}`}</option>
        ))}
      </select>

      <label className={styles.fieldLabel}>{t('schedule.group', 'Группа')}</label>
      <select
        className={styles.select}
        value={activeGroupId}
        onChange={(e) => setActiveGroupId(e.target.value)}
        aria-label={t('schedule.group', 'Группа')}
      >
        <option value="">{t('schedule.all_groups', 'Все группы')}</option>
        {groups.map((g) => (
          <option key={g.id} value={g.id}>{g.name || g.title}</option>
        ))}
      </select>

      <label className={styles.fieldLabel}>{t('schedule.week_parity', 'Чётность недели')}</label>
      <div className={styles.parityToggle} role="radiogroup">
        {WEEK_PARITIES.map((p) => (
          <button
            key={p.value}
            type="button"
            role="radio"
            aria-checked={weekParity === p.value}
            className={[styles.parityBtn, weekParity === p.value ? styles.parityBtnActive : ''].join(' ')}
            onClick={() => setWeekParity(p.value)}
          >
            {p.label}
          </button>
        ))}
      </div>

      <label className={styles.fieldLabel}>{t('schedule.week_anchor', 'Неделя')}</label>
      <div className={styles.weekNav}>
        <button type="button" className={styles.iconBtn} onClick={() => setAnchor(addDays(anchor, -7))} aria-label={t('schedule.prev_week', 'Прошлая неделя')}>
          <Icon name="chevron-left" size={16} />
        </button>
        <span className={styles.weekRange}>{fmtDate(weekDates[0])} — {fmtDate(weekDates[5])}</span>
        <button type="button" className={styles.iconBtn} onClick={() => setAnchor(addDays(anchor, 7))} aria-label={t('schedule.next_week', 'Следующая неделя')}>
          <Icon name="chevron-right" size={16} />
        </button>
      </div>

      <div className={styles.divider} />

      <div className={styles.sectionTitle}>
        <Icon name="book" size={14} /> {t('schedule.unscheduled', 'Не размещено')}
        <span className={styles.countPill}>{unscheduledSubjects.length}</span>
      </div>
      <div className={styles.subjectList} role="list">
        {unscheduledSubjects.length === 0 ? (
          <EmptyState iconName="check-circle" title={t('schedule.all_placed', 'Все дисциплины размещены')} description="" />
        ) : (
          unscheduledSubjects.map(renderSubjectCard)
        )}
      </div>
    </aside>
  )

  const Toolbar = (
    <div className={styles.toolbar} role="toolbar" aria-label={t('schedule.toolbar', 'Панель действий')}>
      <UnvInput
        size="sm"
        iconLeft="search"
        placeholder={t('schedule.filter_teacher', 'Фильтр: преподаватель')}
        value={filter.teacher}
        onChange={(e) => setFilter((f) => ({ ...f, teacher: e.target.value }))}
        fullWidth={false}
        className={styles.filterInput}
      />
      <select
        className={styles.select}
        value={filter.auditorium}
        onChange={(e) => setFilter((f) => ({ ...f, auditorium: e.target.value }))}
        aria-label={t('schedule.filter_auditorium', 'Аудитория')}
      >
        <option value="">{t('schedule.all_auditoriums', 'Все аудитории')}</option>
        {auditoriums.map((a) => (
          <option key={a.id} value={a.id}>{a.name || a.number}</option>
        ))}
      </select>
      <select
        className={styles.select}
        value={filter.subject}
        onChange={(e) => setFilter((f) => ({ ...f, subject: e.target.value }))}
        aria-label={t('schedule.filter_subject', 'Дисциплина')}
      >
        <option value="">{t('schedule.all_subjects', 'Все дисциплины')}</option>
        {subjects.map((s) => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </select>

      <div className={styles.toolbarSpacer} />

      {selectedCells.size > 0 && (
        <UnvButton variant="secondary" size="sm" iconLeft="copy" onClick={copyToNextWeek}>
          {t('schedule.copy_next_week', 'Скопировать в следующую неделю')} ({selectedCells.size})
        </UnvButton>
      )}
      <UnvButton variant="ghost" size="sm" iconLeft="refresh" onClick={reloadSlots}>{t('common.refresh', 'Обновить')}</UnvButton>
      <UnvButton variant="ghost" size="sm" iconLeft="download" onClick={handlePrint}>{t('schedule.print', 'Печать / PDF')}</UnvButton>
      {isAdmin && (
        <UnvButton variant="primary" size="sm" iconLeft="zap" onClick={handleExpand}>{t('schedule.expand', 'Разложить семестр')}</UnvButton>
      )}
    </div>
  )

  const RightPanel = (
    <aside className={[styles.right, rightOpen ? '' : styles.rightCollapsed].filter(Boolean).join(' ')} aria-label={t('schedule.conflicts_panel', 'Панель конфликтов')}>
      <div className={styles.rightHeader}>
        <span className={styles.sectionTitle}>
          <Icon name="alert-circle" size={14} /> {t('schedule.conflicts', 'Конфликты')}
          <span className={styles.countPill}>{conflicts.length}</span>
        </span>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={() => setRightOpen((v) => !v)}
          aria-label={rightOpen ? t('schedule.collapse', 'Свернуть') : t('schedule.expand_panel', 'Развернуть')}
        >
          <Icon name={rightOpen ? 'chevron-right' : 'chevron-left'} size={16} />
        </button>
      </div>
      {rightOpen && (
        <>
          <div className={styles.conflictsList}>
            {conflicts.length === 0 ? (
              <EmptyState iconName="check-circle" title={t('schedule.no_conflicts', 'Конфликтов нет')} description="" />
            ) : (
              conflicts.map((c, i) => (
                <Card key={i} padding="sm" className={styles.conflictItem}>
                  <Badge variant="danger" size="sm">
                    {c.kind === 'teacher' ? t('schedule.conflict_teacher', 'Преподаватель') : c.kind === 'auditorium' ? t('schedule.conflict_room', 'Аудитория') : t('schedule.conflict_group', 'Группа')}
                  </Badge>
                  <div className={styles.conflictBody}>
                    {c.items.map((s) => (
                      <div key={s.id} className={styles.conflictRow}>
                        <Icon name="alert-circle" size={12} />
                        {wdLabel(s.weekday - 1)} · {s.starts_at} · {subjects.find((x) => String(x.id) === String(s.subject?.id || s.subject))?.name || '—'}
                      </div>
                    ))}
                  </div>
                </Card>
              ))
            )}
          </div>
          <div className={styles.divider} />
          <div className={styles.sectionTitle}>
            <Icon name="zap" size={14} /> {t('schedule.actions', 'Действия')}
          </div>
          <UnvButton fullWidth variant="secondary" size="sm" iconLeft="plus" onClick={() => openCreate(1, PAIR_TIMES[0])}>
            {t('schedule.add_lesson', 'Добавить пару')}
          </UnvButton>
          <UnvButton fullWidth variant="ghost" size="sm" iconLeft="download" onClick={handlePrint}>
            {t('schedule.print', 'Печать / PDF')}
          </UnvButton>
        </>
      )}
    </aside>
  )

  // ---- grid ----
  const renderGrid = () => (
    <div
      className={styles.grid}
      role="grid"
      aria-label={t('schedule.grid', 'Расписание (сетка)')}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className={styles.gridHeaderCorner} />
      {WEEKDAY_KEYS.map(([k, fb], i) => (
        <div
          key={k}
          className={[styles.gridHeaderDay, i + 1 === todayKey ? styles.gridHeaderToday : ''].join(' ')}
          role="columnheader"
        >
          <div className={styles.dayLabel}>{t(k, fb)}</div>
          <div className={styles.dayDate}>{fmtDate(weekDates[i])}</div>
        </div>
      ))}

      {PAIR_TIMES.map((pair, idx) => (
        <div key={pair.pair_no} className={styles.gridRow} role="row" style={{ gridRow: idx + 2 }}>
          <div className={styles.gridTimeCol} role="rowheader">
            <span className={styles.timeNo}>{pair.pair_no}</span>
            <span className={styles.timeStart}>{TIME_SLOTS[idx]}</span>
            <span className={styles.timeEnd}>{pair.ends_at}</span>
          </div>
        </div>
      ))}

      {PAIR_TIMES.map((pair, pIdx) => WEEKDAY_KEYS.map((_, dIdx) => {
        const weekday = dIdx + 1
        const key = `${weekday}-${pair.pair_no}`
        const cell = grid[key] || []
        const isOver = dragOverKey === key
        const isSelected = selectedCells.has(key)
        return (
          <div
            key={key}
            data-cell={key}
            tabIndex={0}
            role="gridcell"
            aria-label={`${wdLabel(dIdx)} ${TIME_SLOTS[pIdx]}`}
            className={[
              styles.gridCell,
              isOver ? styles.dropTarget : '',
              isSelected ? styles.cellSelected : '',
              cell.length === 0 ? styles.cellEmpty : '',
            ].filter(Boolean).join(' ')}
            style={{ gridRow: pIdx + 2, gridColumn: dIdx + 2 }}
            onDragOver={(e) => handleCellDragOver(e, key)}
            onDragLeave={handleCellDragLeave}
            onDrop={(e) => handleCellDrop(e, weekday, pair)}
            onClick={(e) => {
              if (e.shiftKey) toggleCellSelect(key, true)
            }}
            onKeyDown={(e) => handleCellKey(e, weekday, pair)}
          >
            {cell.length === 0 && isAdmin && (
              <button
                type="button"
                className={styles.quickAdd}
                aria-label={t('schedule.quick_add', 'Добавить пару')}
                onClick={(e) => { e.stopPropagation(); openCreate(weekday, pair) }}
              >
                <Icon name="plus" size={14} />
              </button>
            )}
            {cell.map(renderLessonCard)}
          </div>
        )
      }))}
    </div>
  )

  // ---- mobile day list ----
  const renderMobileDays = () => (
    <div className={styles.mobileDays}>
      {WEEKDAY_KEYS.map(([k, fb], i) => {
        const weekday = i + 1
        return (
          <Card key={k} padding="sm" className={styles.mobileDayCard}>
            <div className={styles.mobileDayHeader}>
              <strong>{t(k, fb)}</strong> <span className={styles.muted}>{fmtDate(weekDates[i])}</span>
            </div>
            {PAIR_TIMES.map((pair, pIdx) => {
              const key = `${weekday}-${pair.pair_no}`
              const cell = grid[key] || []
              return (
                <div key={pair.pair_no} className={styles.mobileSlotRow}>
                  <span className={styles.mobileTime}>{TIME_SLOTS[pIdx]}</span>
                  <div className={styles.mobileCell}>
                    {cell.length === 0 ? (
                      isAdmin && (
                        <button type="button" className={styles.mobileAddBtn} onClick={() => openCreate(weekday, pair)}>
                          <Icon name="plus" size={14} /> {t('schedule.add', 'Добавить')}
                        </button>
                      )
                    ) : (
                      cell.map(renderLessonCard)
                    )}
                  </div>
                </div>
              )
            })}
          </Card>
        )
      })}
    </div>
  )

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('schedule.builder.title', 'Конструктор расписания')}
        subtitle={t('schedule.builder.subtitle', 'Перетаскивайте дисциплины, проверяйте конфликты в реальном времени')}
        actions={
          <UnvButton variant="primary" iconLeft="plus" onClick={() => openCreate(1, PAIR_TIMES[0])} disabled={!isAdmin}>
            {t('schedule.new_slot', 'Новая пара')}
          </UnvButton>
        }
      />

      {!isMobile && Toolbar}

      {isMobile && (
        <div className={styles.mobileTabs} role="tablist">
          {MOBILE_TAB_KEYS.map((tb) => (
            <button
              key={tb.key}
              type="button"
              role="tab"
              aria-selected={mobileTab === tb.key}
              className={[styles.mobileTab, mobileTab === tb.key ? styles.mobileTabActive : ''].join(' ')}
              onClick={() => setMobileTab(tb.key)}
            >
              <Icon name={tb.icon} size={16} /> {t(tb.i18n, tb.fb)}
            </button>
          ))}
        </div>
      )}

      <div className={[styles.layout, isMobile ? styles.layoutMobile : ''].filter(Boolean).join(' ')}>
        {(!isMobile || mobileTab === 'groups') && Sidebar}

        {(!isMobile || mobileTab === 'lessons') && (
          <main className={styles.main} aria-busy={busy || undefined}>
            {busy && slots.length === 0 ? (
              <Card><Skeleton variant="table" rows={8} cols={7} /></Card>
            ) : loadError ? (
              <ErrorState title={t('schedule.load_failed', 'Не удалось загрузить')} onRetry={reloadSlots} />
            ) : (
              <Card padding="none" className={styles.gridCard}>
                {isMobile ? renderMobileDays() : renderGrid()}
              </Card>
            )}
          </main>
        )}

        {(!isMobile || mobileTab === 'conflicts') && RightPanel}
      </div>

      {/* Edit modal */}
      <UnvModal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? t('schedule.edit_slot', 'Редактирование пары') : t('schedule.new_slot', 'Новая пара')}
        size="md"
      >
        {editing && (
          <div className={styles.formGrid}>
            <label className={styles.fieldLabel}>{t('schedule.subject', 'Дисциплина')}</label>
            <select
              className={styles.select}
              value={editing.subject || ''}
              onChange={(e) => setEditing({ ...editing, subject: Number(e.target.value) })}
            >
              <option value="">—</option>
              {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>

            <label className={styles.fieldLabel}>{t('schedule.teacher', 'Преподаватель')}</label>
            <select
              className={styles.select}
              value={editing.teacher || ''}
              onChange={(e) => setEditing({ ...editing, teacher: Number(e.target.value) })}
            >
              <option value="">—</option>
              {teachers.map((tch) => <option key={tch.id} value={tch.id}>{tch.full_name || tch.name}</option>)}
            </select>

            <label className={styles.fieldLabel}>{t('schedule.auditorium', 'Аудитория')}</label>
            <select
              className={styles.select}
              value={editing.auditorium || ''}
              onChange={(e) => setEditing({ ...editing, auditorium: Number(e.target.value) })}
            >
              <option value="">—</option>
              {auditoriums.map((a) => <option key={a.id} value={a.id}>{a.name || a.number}</option>)}
            </select>

            <div className={styles.row2}>
              <div>
                <label className={styles.fieldLabel}>{t('schedule.weekday', 'День')}</label>
                <select className={styles.select} value={editing.weekday} onChange={(e) => setEditing({ ...editing, weekday: Number(e.target.value) })}>
                  {WEEKDAYS.slice(0, 6).map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
                </select>
              </div>
              <div>
                <label className={styles.fieldLabel}>{t('schedule.pair', 'Пара')}</label>
                <select className={styles.select} value={editing.pair_no} onChange={(e) => {
                  const p = PAIR_TIMES.find((x) => x.pair_no === Number(e.target.value))
                  setEditing({ ...editing, pair_no: p.pair_no, starts_at: p.starts_at, ends_at: p.ends_at })
                }}>
                  {PAIR_TIMES.map((p) => <option key={p.pair_no} value={p.pair_no}>{p.pair_no}. {p.starts_at}–{p.ends_at}</option>)}
                </select>
              </div>
            </div>

            <div className={styles.row2}>
              <div>
                <label className={styles.fieldLabel}>{t('schedule.lesson_type', 'Тип занятия')}</label>
                <select className={styles.select} value={editing.lesson_type} onChange={(e) => setEditing({ ...editing, lesson_type: e.target.value })}>
                  {LESSON_TYPES.map((lt) => <option key={lt.value} value={lt.value}>{lt.label}</option>)}
                </select>
              </div>
              <div>
                <label className={styles.fieldLabel}>{t('schedule.week_parity', 'Чётность')}</label>
                <select className={styles.select} value={editing.week_parity} onChange={(e) => setEditing({ ...editing, week_parity: e.target.value })}>
                  {WEEK_PARITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>

            <label className={styles.fieldLabel}>{t('schedule.note', 'Примечание')}</label>
            <textarea
              className={styles.textarea}
              rows={2}
              value={editing.note || ''}
              onChange={(e) => setEditing({ ...editing, note: e.target.value })}
            />
          </div>
        )}
        <UnvModal.Footer>
          {editing?.id && (
            <UnvButton variant="danger" iconLeft="delete" onClick={handleDelete} disabled={busy}>
              {t('common.delete', 'Удалить')}
            </UnvButton>
          )}
          <UnvButton variant="ghost" onClick={() => setEditing(null)}>{t('common.cancel', 'Отмена')}</UnvButton>
          <UnvButton variant="primary" iconLeft="save" onClick={handleSave} loading={busy}>
            {t('common.save', 'Сохранить')}
          </UnvButton>
        </UnvModal.Footer>
      </UnvModal>

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}
